import React, { useEffect, useState, useCallback, Children, isValidElement, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import './index.scss';

interface PageTransitionProps {
  children: React.ReactNode;
  timeout?: number;
  classNames?: string;
}

// 全局 key 映射，确保每个路由路径都有稳定的 key
const pathKeyMap = new Map<string, number>();
let keyCounter = 0;

const getStableKey = (pathname: string): number => {
  if (!pathKeyMap.has(pathname)) {
    pathKeyMap.set(pathname, keyCounter++);
  }
  return pathKeyMap.get(pathname)!;
};

const PageTransition: React.FC<PageTransitionProps> = ({ 
  children, 
  timeout = 300,
  classNames = 'page-transition'
}) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const prevPathRef = useRef(location.pathname);

  // 检测是否包含KeepAlive组件 - 更可靠的检测方法
  const hasKeepAlive = Children.toArray(children).some(child => {
    if (!isValidElement(child)) return false;
    
    // 检查组件类型
    const componentType = child.type;
    
    // 检查是否是KeepAlive组件
    if (typeof componentType === 'function') {
      // 检查组件名称
      if (componentType.name === 'KeepAlive') return true;
      
      // 检查组件displayName
      if ((componentType as any).displayName === 'KeepAlive') return true;
      
      // 检查组件toString结果
      const componentString = componentType.toString();
      if (componentString.includes('KeepAlive')) return true;
    }
    
    // 检查props中是否有name属性（KeepAlive的特征）
    if (child.props && child.props.name) {
      return true;
    }
    
    return false;
  });

  // 获取当前路径的稳定 key
  const stableKey = getStableKey(location.pathname);

  useEffect(() => {
    // 只有当路径真正改变时才触发动画
    if (prevPathRef.current === location.pathname) {
      return;
    }
    prevPathRef.current = location.pathname;

    if (hasKeepAlive) {
      // 对于KeepAlive组件，只控制显示/隐藏状态
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      
      return () => clearTimeout(timer);
    }
    // 对于普通组件，不再改变 key，让 CSSTransition 使用稳定的 key
  }, [location.pathname, hasKeepAlive]);

  const handleEnter = useCallback(() => {
    console.log('PageTransition: 组件进入动画 - 路径:', location.pathname);
  }, [location.pathname]);

  const handleExit = useCallback(() => {
    console.log('PageTransition: 组件退出动画 - 路径:', location.pathname);
  }, [location.pathname]);

  // 如果是KeepAlive模式，使用简单的CSS过渡
  if (hasKeepAlive) {
    return (
      <div 
        className={`page-transition-wrapper ${isVisible ? 'page-transition-visible' : 'page-transition-hidden'}`}
        style={{ 
          transition: `opacity ${timeout}ms ease-in-out`,
          opacity: isVisible ? 1 : 0
        }}
      >
        {children}
      </div>
    );
  }

  // 普通模式，使用react-transition-group，但使用稳定的 key
  return (
    <TransitionGroup component={null}>
      <CSSTransition
        key={stableKey} // 使用稳定的 key，避免组件重新创建
        timeout={timeout}
        classNames={classNames}
        unmountOnExit={false} // 改为 false，避免组件被卸载
        onEnter={handleEnter}
        onExit={handleExit}
      >
        <div className="page-transition-wrapper">
          {children}
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};

export default PageTransition;
